import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Search, Users, Send, Link2, UserPlus, Smartphone } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { useSession } from '../../session/SessionProvider';
import { errorText } from '../../lib/errors';
import { PageHeader, DataTable, Avatar, Badge, Input, Select, EmptyState, Tabs, Button } from '../../ui';
import EmployeeDrawer from './EmployeeDrawer';
import InvitesPanel, { InviteDialog } from './InvitesPanel';

const PAGE_SIZE = 25;

export const employeeName = (u) => [u.last_name, u.first_name].filter(Boolean).join(' ').trim();

/**
 * Сотрудники: в консоли владельца — вся платформа (с фильтром по компании),
 * в кабинете компании — только её сотрудники (ограничивает сервер).
 */
export default function EmployeesPage() {
  const { t, fmt } = useI18n();
  const { isOwner, isViewingAsOwner, company, canEdit } = useSession();
  const platformWide = isOwner && !isViewingAsOwner;
  // Приглашения — в кабинете компании (у владельца в общей консоли компании не выбрано)
  const [params, setParams] = useSearchParams();
  const tab = !platformWide && params.get('tab') === 'invites' ? 'invites' : 'list';
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invitesVersion, setInvitesVersion] = useState(0);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [source, setSource] = useState('all');
  const [sex, setSex] = useState('all');
  const [app, setApp] = useState(params.get('app') || 'all');
  const [appInfo, setAppInfo] = useState(null);
  const [companyId, setCompanyId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const id = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    if (!platformWide) return;
    api.get('/admin/tenants').then((res) => setCompanies(res.data.data || [])).catch(() => {});
  }, [platformWide]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/users', {
        params: { page, limit: PAGE_SIZE, ...(debounced ? { search: debounced } : {}), ...(source !== 'all' ? { source } : {}), ...(sex !== 'all' ? { sex } : {}), ...(app !== 'all' ? { app } : {}) },
        // Фильтр по компании для владельца — тем же механизмом, что и контекст компании
        headers: platformWide && companyId ? { 'X-Admin-Tenant-ID': companyId } : undefined,
      });
      setRows(res.data.users || []);
      setTotal(res.data.total || 0);
      setAppInfo({ latest: res.data.app_latest || null, counts: res.data.app_counts || null });
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setLoading(false);
    }
  }, [page, debounced, source, sex, app, companyId, platformWide, t]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    {
      key: 'name',
      header: t('employees.employee'),
      render: (u) => {
        const name = employeeName(u);
        return (
          <div className="flex items-center gap-3">
            <Avatar name={name || u.phone} src={u.avatar_url || undefined} size="sm" />
            <div className="min-w-0">
              <p className="truncate font-medium text-zinc-900 dark:text-zinc-100">{name || '—'}</p>
              <p className="tabular text-xs text-zinc-500">{u.phone}</p>
            </div>
          </div>
        );
      },
    },
    ...(platformWide ? [{ key: 'tenant_name', header: t('employees.company'), hideOnMobile: true, render: (u) => u.tenant_name }] : []),
    { key: 'email', header: t('common.email'), hideOnMobile: true, render: (u) => <span className="text-zinc-500">{u.email || '—'}</span> },
    {
      key: 'registered',
      header: t('employees.registered'),
      render: (u) => (
        <div className="flex flex-wrap items-center gap-1.5">
          {u.email ? <Badge tone="success" dot>{t('employees.registeredYes')}</Badge> : <Badge tone="neutral" dot>{t('employees.registeredNo')}</Badge>}
          {u.reg_source === 'telegram' && <Badge tone="brand"><Send className="h-3 w-3" />Telegram</Badge>}
        </div>
      ),
    },
    {
      key: 'sex',
      header: t('employees.sex'),
      hideOnMobile: true,
      render: (u) => (u.sex === 1 || u.sex === 2
        ? <span className="text-zinc-700 dark:text-zinc-300">{t(`employees.sexes.${u.sex}`)}</span>
        : <span className="text-zinc-400">—</span>),
    },
    {
      key: 'app_version',
      header: t('employees.app'),
      hideOnMobile: true,
      render: (u) => <AppVersionCell user={u} latest={appInfo?.latest} />,
    },
    {
      key: 'last_login_at',
      header: t('employees.lastActive'),
      hideOnMobile: true,
      render: (u) => {
        // Остання активність у застосунку; для старих записів — останній вхід
        const last = [u.last_seen_at, u.last_login_at].filter(Boolean).sort().pop();
        return <span className="tabular text-zinc-500">{last ? fmt.relative(last) : t('common.never')}</span>;
      },
    },
    {
      key: 'created_at',
      header: t('employees.joined'),
      hideOnMobile: true,
      render: (u) => <span className="tabular text-zinc-500">{fmt.date(u.created_at)}</span>,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('employees.title')}
        description={platformWide ? t('employees.subtitleOwner') : t('employees.subtitleCompany')}
        actions={!platformWide && canEdit && <Button icon={UserPlus} onClick={() => setInviteOpen(true)}>{t('invites.invite')}</Button>}
      >
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <p className="tabular text-xs text-zinc-400">{t('common.total', { count: fmt.number(total) })}</p>
          {appInfo?.latest && appInfo.counts?.outdated > 0 && app !== 'outdated' && (
            <button type="button" onClick={() => { setApp('outdated'); setPage(1); }} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-amber-200 hover:bg-amber-100 dark:bg-amber-950/50 dark:text-amber-300 dark:ring-amber-900">
              <Smartphone className="h-3 w-3" />{t('employees.outdatedChip', { version: appInfo.latest, count: fmt.number(appInfo.counts.outdated) })}
            </button>
          )}
        </div>
      </PageHeader>

      {!platformWide && (
        <Tabs
          className="mb-5"
          value={tab}
          onChange={(v) => setParams(v === 'list' ? {} : { tab: v }, { replace: true })}
          items={[
            { id: 'list', label: t('employees.title'), icon: Users },
            { id: 'invites', label: t('invites.tab'), icon: Link2 },
          ]}
        />
      )}

      {!platformWide && company && (
        <InviteDialog
          open={inviteOpen}
          tenantId={company.id}
          onClose={() => setInviteOpen(false)}
          onCreated={() => setInvitesVersion((v) => v + 1)}
        />
      )}

      {tab === 'invites' ? (
        <InvitesPanel tenantId={company.id} version={invitesVersion} onInvite={() => setInviteOpen(true)} />
      ) : (
      <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="sm:w-80">
          <Input icon={Search} value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('employees.searchPlaceholder')} aria-label={t('common.search')} />
        </div>
        {platformWide && (
          <Select value={companyId} onChange={(e) => { setCompanyId(e.target.value); setPage(1); }} className="sm:w-56" aria-label={t('employees.company')}>
            <option value="">{t('overview.allCompanies')}</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        )}
        <Select value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }} className="sm:w-48" aria-label={t('employees.source')}>
          {['all', 'app', 'telegram'].map((s) => <option key={s} value={s}>{t(`employees.sources.${s}`)}</option>)}
        </Select>
        <Select value={app} onChange={(e) => { setApp(e.target.value); setPage(1); }} className="sm:w-52" aria-label={t('employees.app')}>
          {['all', 'outdated', 'current', 'unknown'].map((s) => <option key={s} value={s}>{t(`employees.appFilter.${s}`)}</option>)}
        </Select>
        <Select value={sex} onChange={(e) => { setSex(e.target.value); setPage(1); }} className="sm:w-44" aria-label={t('employees.sex')}>
          <option value="all">{t('employees.sexAll')}</option>
          {['2', '1', '0'].map((s) => <option key={s} value={s}>{t(`employees.sexes.${s}`)}</option>)}
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        rowKey={(u) => u.user_id}
        onRowClick={(u) => setOpenId(u.user_id)}
        empty={<EmptyState icon={Users} title={t('employees.empty')} text={t('employees.emptyHint')} />}
        pagination={{ page, pages: Math.max(1, Math.ceil(total / PAGE_SIZE)), onPage: setPage }}
      />
      </>
      )}

      <EmployeeDrawer userId={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </>
  );
}

/** Версія застосунку: «1.5» і позначка, якщо не оновили до актуальної в App Store */
function AppVersionCell({ user, latest }) {
  const { t } = useI18n();
  if (!user.app_version) return <span className="text-zinc-400">—</span>;
  const legacy = /^iOS-EnjoyFb\//.test(user.app_version);
  const version = legacy ? null : (String(user.app_version).match(/(\d+(?:\.\d+){0,2})/) || [])[1];
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="tabular text-zinc-700 dark:text-zinc-300">{legacy ? t('employees.legacyAppShort') : version}</span>
      {user.app_status === 'outdated' && <Badge tone="warning">{t('employees.outdated')}</Badge>}
      {user.app_status === 'current' && latest && <Badge tone="success">{t('employees.upToDateBadge')}</Badge>}
    </div>
  );
}
